// ─────────────────────────────────────────────
// Oike — time zones people can recognize. Pure, shared by every screen
// (Mi día, availability, people, the participant portal) and by the tests.
//
// What is stored is always an IANA identifier ("America/La_Paz"); what people
// see and search is the country and the city ("Bolivia — La Paz"). Search
// ignores accents and case, and also matches the identifier. Every option is
// checked against the runtime (Intl) so an unknown zone is never offered.
// Dates are stored in UTC: changing a preference changes how they are shown,
// never the moment they happen.
// ─────────────────────────────────────────────
import { isValidTimeZone, tzOffsetMinutes } from './coachingTime.js';

/** [country, city, IANA id, extra search words] — the places Oike's people are most likely in, first. */
const CURATED = [
  ['Argentina', 'Buenos Aires', 'America/Argentina/Buenos_Aires', 'cordoba rosario mendoza'],
  ['Bolivia', 'La Paz', 'America/La_Paz', 'santa cruz cochabamba sucre'],
  ['Brasil', 'São Paulo', 'America/Sao_Paulo', 'brazil rio de janeiro brasilia'],
  ['Brasil', 'Manaos', 'America/Manaus', 'brazil manaus amazonas'],
  ['Chile', 'Santiago', 'America/Santiago', ''],
  ['Colombia', 'Bogotá', 'America/Bogota', 'medellin cali'],
  ['Costa Rica', 'San José', 'America/Costa_Rica', ''],
  ['Cuba', 'La Habana', 'America/Havana', 'havana'],
  ['Ecuador', 'Quito', 'America/Guayaquil', 'guayaquil'],
  ['El Salvador', 'San Salvador', 'America/El_Salvador', ''],
  ['Guatemala', 'Ciudad de Guatemala', 'America/Guatemala', ''],
  ['Honduras', 'Tegucigalpa', 'America/Tegucigalpa', ''],
  ['México', 'Ciudad de México', 'America/Mexico_City', 'mexico cdmx guadalajara monterrey'],
  ['México', 'Tijuana', 'America/Tijuana', 'mexico baja california'],
  ['México', 'Cancún', 'America/Cancun', 'mexico quintana roo'],
  ['Nicaragua', 'Managua', 'America/Managua', ''],
  ['Panamá', 'Ciudad de Panamá', 'America/Panama', ''],
  ['Paraguay', 'Asunción', 'America/Asuncion', ''],
  ['Perú', 'Lima', 'America/Lima', 'peru'],
  ['Puerto Rico', 'San Juan', 'America/Puerto_Rico', ''],
  ['República Dominicana', 'Santo Domingo', 'America/Santo_Domingo', 'dominicana'],
  ['Uruguay', 'Montevideo', 'America/Montevideo', ''],
  ['Venezuela', 'Caracas', 'America/Caracas', ''],
  ['España', 'Madrid', 'Europe/Madrid', 'spain barcelona valencia sevilla bilbao'],
  ['España', 'Canarias', 'Atlantic/Canary', 'spain las palmas tenerife'],
  ['Portugal', 'Lisboa', 'Europe/Lisbon', 'lisbon'],
  ['Andorra', 'Andorra la Vella', 'Europe/Andorra', ''],
  ['Francia', 'París', 'Europe/Paris', 'france paris'],
  ['Italia', 'Roma', 'Europe/Rome', 'italy milan'],
  ['Alemania', 'Berlín', 'Europe/Berlin', 'germany berlin munich'],
  ['Países Bajos', 'Ámsterdam', 'Europe/Amsterdam', 'netherlands holanda amsterdam'],
  ['Bélgica', 'Bruselas', 'Europe/Brussels', 'belgium'],
  ['Suiza', 'Zúrich', 'Europe/Zurich', 'switzerland ginebra'],
  ['Reino Unido', 'Londres', 'Europe/London', 'uk england inglaterra london'],
  ['Irlanda', 'Dublín', 'Europe/Dublin', 'ireland'],
  ['Estados Unidos', 'Nueva York', 'America/New_York', 'usa eeuu new york miami washington boston este'],
  ['Estados Unidos', 'Chicago', 'America/Chicago', 'usa eeuu texas dallas houston centro'],
  ['Estados Unidos', 'Denver', 'America/Denver', 'usa eeuu montaña'],
  ['Estados Unidos', 'Phoenix', 'America/Phoenix', 'usa eeuu arizona'],
  ['Estados Unidos', 'Los Ángeles', 'America/Los_Angeles', 'usa eeuu california san francisco pacifico'],
  ['Canadá', 'Toronto', 'America/Toronto', 'canada'],
  ['Canadá', 'Vancouver', 'America/Vancouver', 'canada'],
  ['Marruecos', 'Casablanca', 'Africa/Casablanca', 'morocco'],
  ['Israel', 'Jerusalén', 'Asia/Jerusalem', 'tel aviv'],
  ['Emiratos Árabes Unidos', 'Dubái', 'Asia/Dubai', 'dubai uae'],
  ['India', 'Calcuta', 'Asia/Kolkata', 'kolkata delhi mumbai'],
  ['China', 'Shanghái', 'Asia/Shanghai', 'beijing pekin'],
  ['Japón', 'Tokio', 'Asia/Tokyo', 'japan tokyo'],
  ['Australia', 'Sídney', 'Australia/Sydney', 'sydney'],
  ['Nueva Zelanda', 'Auckland', 'Pacific/Auckland', 'new zealand'],
  ['Tiempo universal', 'UTC', 'UTC', 'gmt utc'],
];

const REGION_ES = { America: 'América', Europe: 'Europa', Asia: 'Asia', Africa: 'África', Australia: 'Australia', Pacific: 'Pacífico', Atlantic: 'Atlántico', Indian: 'Índico', Antarctica: 'Antártida', Arctic: 'Ártico' };

export const normalize = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[_/]+/g, ' ').replace(/\s+/g, ' ').trim();

let cache = null;
/** Every zone people can pick: the curated ones first, then the rest of the IANA zones the runtime knows. */
export function timeZoneOptions() {
  if (cache) return cache;
  const seen = new Set();
  const out = [];
  for (const [country, city, id, extra] of CURATED) {
    if (!isValidTimeZone(id)) continue;
    out.push({ id, label: `${country} — ${city}`, country, city, search: normalize(`${country} ${city} ${id} ${extra}`), curated: true });
    seen.add(id);
  }
  let all = [];
  try { all = Intl.supportedValuesOf('timeZone'); } catch { /* older runtimes: only the curated list */ }
  for (const id of all) {
    if (seen.has(id) || !isValidTimeZone(id)) continue;
    const parts = id.split('/');
    const city = parts[parts.length - 1].replace(/_/g, ' ');
    const region = REGION_ES[parts[0]] || parts[0];
    out.push({ id, label: `${region} — ${city}`, country: region, city, search: normalize(`${region} ${parts.join(' ')} ${id}`), curated: false });
    seen.add(id);
  }
  cache = out;
  return out;
}

/** The options that match a search ("bolivia", "la paz", "madrid", "America/La_Paz"); every word must match. */
export function searchTimeZones(query, limit = 30) {
  const words = normalize(query).split(' ').filter(Boolean);
  const list = timeZoneOptions();
  if (!words.length) return list.filter(o => o.curated).slice(0, limit);
  return list.filter(o => words.every(w => o.search.includes(w))).slice(0, limit);
}

/** The option of a stored id, or null if it is not a valid IANA zone (never a guess). */
export function timeZoneOption(id) {
  if (!isValidTimeZone(id)) return null;
  return timeZoneOptions().find(o => o.id === id) || { id, label: id, country: '', city: id, search: normalize(id), curated: false };
}

const fmtOffset = (min) => `UTC${min >= 0 ? '+' : '−'}${String(Math.floor(Math.abs(min) / 60)).padStart(2, '0')}:${String(Math.abs(min) % 60).padStart(2, '0')}`;

/** What the zone looks like now: local time, offset and whether it changes during the year. */
export function timeZonePreview(id, now = new Date()) {
  if (!isValidTimeZone(id)) return null;
  const time = now.toLocaleTimeString('es', { timeZone: id, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  const day = now.toLocaleDateString('es', { timeZone: id, weekday: 'long', day: 'numeric', month: 'long' });
  const offset = tzOffsetMinutes(now, id);
  const y = now.getUTCFullYear();
  const jan = tzOffsetMinutes(new Date(Date.UTC(y, 0, 15)), id), jul = tzOffsetMinutes(new Date(Date.UTC(y, 6, 15)), id);
  return { time, day, offset: fmtOffset(offset), seasonal: jan !== jul };
}
